import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Typography,
  CircularProgress,
  Switch,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  Box,
  Tooltip,
  Divider,
  Link,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Send as SendIcon,
  ContentCopy as CopyIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { deliveryService } from '../../services/deliveryService';
import connectionsService from '../../services/connectionsService';
import { openOAuthPopup } from '../../utils/oauthPopup';
import { useAuth } from '../../contexts/AuthContext';
import { useEnvironment } from '../../contexts/EnvironmentContext';
import { useEntitlements } from '../../contexts/EntitlementsContext';
import { track } from '../../utils/analytics';
import { iconFor } from './connectorIcons';

const STATUS_COLOR = { SUCCESS: 'success', FAILED: 'error', PENDING: 'default' };

const EVENT_LABELS = {
  SUBMISSION: 'Submissions',
  BOOKING_CREATED: 'Booking created',
  BOOKING_CANCELLED: 'Booking cancelled',
};

// The checkbox groups the add form offers. A connector's `supports()` decides which of these
// (if any) actually show up, a connector that only ever carries bookings shows just the second.
const EVENT_GROUPS = [
  { key: 'submission', label: 'Form submissions', events: ['SUBMISSION'] },
  { key: 'booking', label: 'Bookings', events: ['BOOKING_CREATED', 'BOOKING_CANCELLED'] },
];

const eventGroupsFor = (supports = []) => EVENT_GROUPS
  .map((group) => ({ ...group, events: group.events.filter((e) => supports.includes(e)) }))
  .filter((group) => group.events.length > 0);

const CONNECT_POLL_MS = 3000;
const CONNECT_POLL_TIMEOUT_MS = 120000;

const providerLabel = (provider) => (provider
  ? provider.charAt(0).toUpperCase() + provider.slice(1)
  : 'account');

const when = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const hostOf = (url) => {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
};

/** One line that says where this destination goes, whatever type it turns out to be. */
const summaryOf = (destination) => {
  const c = destination.config || {};
  if (destination.type === 'WHATSAPP_REPLY') return 'Reply to customer';
  if (c.spreadsheetUrl) return c.sheetName || 'Spreadsheet';
  if (c.calendarName) return c.calendarName;
  if (c.channel) return c.channel;
  if (c.to) return c.to;
  if (c.url) return hostOf(c.url);
  if (destination.type === 'GOOGLE_SHEETS') return 'Not created';
  return '-';
};

const normalizeChoice = (item) => (typeof item === 'string' ? { value: item, label: item } : item);

/** A connector's config schema plus a connection needs a stand-in field to render, GOOGLE_SHEETS
 * needs an account but has no fields of its own, and a connector with an explicit CONNECTION
 * field in its schema needs no synthetic one added on top. */
const effectiveFieldsFor = (connector) => {
  if (!connector) return [];
  const fields = connector.configSchema?.fields || [];
  const hasConnectionField = fields.some((f) => f.type === 'CONNECTION');
  if (connector.connectionProvider && !hasConnectionField) {
    return [
      {
        key: '_connection',
        label: `${providerLabel(connector.connectionProvider)} account`,
        type: 'CONNECTION',
        required: true,
        placeholder: null,
        help: null,
        maxLength: null,
        choicesEndpoint: null,
      },
      ...fields,
    ];
  }
  return fields;
};

const isDestinationEditable = (connector) => !!connector
  && (connector.configSchema?.fields || []).some((f) => f.type !== 'REVEAL_ONCE');

/**
 * "Where should answers go?", one connector-agnostic destination dialog, driven entirely by
 * `GET /api/connectors`. Reachable from the flows list card's "Deliver answers…" menu item and
 * from the Submissions page once a flow is selected in the filter; also reused, via `scope`, by
 * Settings -> Deliveries for environment-default destinations (docs/connectors/ws-8.md).
 *
 * A connector shipped after this dialog gets a working form with no frontend release: its
 * `configSchema.fields` says what to render, `supports()` says which event checkboxes to offer,
 * and `connectionProvider` says whether it needs a connected account.
 *
 * @param scope 'FLOW' (default, needs `flow`) or 'ENVIRONMENT_DEFAULT' (needs only the selected
 *   environment), decides which REST endpoints the dialog reads and writes, and swaps the
 *   explanatory copy. A FLOW-scoped dialog also loads the environment's defaults so it can show
 *   ones the flow inherits (greyed, with an "Override for this flow" action) alongside its own.
 *
 * Honest UI: destination status always reflects `lastStatus`/the API's test-send response, never
 * an optimistic "Sent"; editing never offers to change `events` because the API does not apply
 * that on update; an EMAIL/WEBHOOK-style connector shows Edit only when it actually has an
 * editable field, matching the server's own read-only rule for connectors like Google Sheets.
 */
const DestinationDialog = ({ open, onClose, flow, scope = 'FLOW' }) => {
  const { authenticatedFetch } = useAuth();
  const { selectedEnvironment } = useEnvironment();
  const { features } = useEntitlements();
  const envId = selectedEnvironment?.id;
  const flowId = flow?.id;
  const isEnvScope = scope === 'ENVIRONMENT_DEFAULT';

  // Dispatches to the flow-scoped or environment-default REST endpoints, so the rest of the
  // component reads and writes destinations without knowing which one it is.
  const api = useMemo(() => (isEnvScope ? {
    list: () => deliveryService.listEnvironmentDestinations(envId, authenticatedFetch),
    create: (body) => deliveryService.createEnvironmentDestination(envId, body, authenticatedFetch),
    update: (id, body) => deliveryService.updateEnvironmentDestination(envId, id, body, authenticatedFetch),
    remove: (id) => deliveryService.deleteEnvironmentDestination(envId, id, authenticatedFetch),
    test: (id) => deliveryService.testEnvironmentDestination(envId, id, authenticatedFetch),
    deliveries: (limit) => deliveryService.listEnvironmentDeliveries(envId, { limit }, authenticatedFetch),
  } : {
    list: () => deliveryService.listDestinations(envId, flowId, authenticatedFetch),
    create: (body) => deliveryService.createDestination(envId, flowId, body, authenticatedFetch),
    update: (id, body) => deliveryService.updateDestination(envId, flowId, id, body, authenticatedFetch),
    remove: (id) => deliveryService.deleteDestination(envId, flowId, id, authenticatedFetch),
    test: (id) => deliveryService.testDestination(envId, flowId, id, authenticatedFetch),
    deliveries: (limit) => deliveryService.listDeliveries(envId, flowId, limit, authenticatedFetch),
  }), [isEnvScope, envId, flowId, authenticatedFetch]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [destinations, setDestinations] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  // Only populated for a FLOW-scoped dialog: the environment's defaults, so the list can show
  // which ones this flow inherits.
  const [environmentDefaults, setEnvironmentDefaults] = useState([]);

  const [connectors, setConnectors] = useState([]);
  const [connectorsError, setConnectorsError] = useState(null);
  const [connections, setConnections] = useState([]);

  // 'list' | 'picker' | 'form'
  const [mode, setMode] = useState('list');
  const [formConnector, setFormConnector] = useState(null);
  const [editingDestination, setEditingDestination] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [formEvents, setFormEvents] = useState(() => new Set());
  const [formConnectionId, setFormConnectionId] = useState(null);
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showSecret, setShowSecret] = useState({});
  const [choices, setChoices] = useState({});
  const [choicesError, setChoicesError] = useState({});

  const [revealed, setRevealed] = useState(null); // { label, value, help }
  const [copied, setCopied] = useState(false);

  const [testingId, setTestingId] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  const [connecting, setConnecting] = useState(false);
  const pollRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current.interval);
      clearTimeout(pollRef.current.timeout);
      pollRef.current = null;
    }
    setConnecting(false);
  }, []);

  const loadAll = useCallback(async () => {
    if (!envId || (!isEnvScope && !flowId)) return;
    setLoading(true);
    setError(null);
    try {
      const [dests, recent, defaults] = await Promise.all([
        api.list(),
        api.deliveries(20),
        // A FLOW-scoped dialog also needs to know the environment's defaults, to show which ones
        // this flow inherits; an environment-default dialog has no "inherited from" of its own.
        isEnvScope ? Promise.resolve([])
          : deliveryService.listEnvironmentDestinations(envId, authenticatedFetch).catch(() => []),
      ]);
      setDestinations(dests || []);
      setDeliveries(recent || []);
      setEnvironmentDefaults(defaults || []);
    } catch (e) {
      setError(e.message || 'Failed to load delivery settings');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [envId, flowId, isEnvScope, api]);

  const loadConnectors = useCallback(async () => {
    try {
      const list = await deliveryService.listConnectors(authenticatedFetch);
      setConnectors(list || []);
      setConnectorsError(null);
    } catch (e) {
      setConnectorsError(e.message || 'Failed to load connector types');
    }
  }, [authenticatedFetch]);

  const loadConnections = useCallback(async () => {
    if (!envId) return [];
    try {
      const list = await connectionsService.list(envId, authenticatedFetch);
      setConnections(list || []);
      return list || [];
    } catch {
      setConnections([]);
      return [];
    }
    // authenticatedFetch is recreated every render, a dependency here would poll in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [envId]);

  const resetForm = () => {
    setFormConnector(null);
    setEditingDestination(null);
    setFormValues({});
    setFormEvents(new Set());
    setFormConnectionId(null);
    setFormError(null);
    setShowSecret({});
  };

  useEffect(() => {
    if (!open) return;
    setMode('list');
    resetForm();
    setRevealed(null);
    setDeleteConfirmId(null);
    loadAll();
    loadConnectors();
    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, envId, flowId]);

  // Fetch a SELECT field's remote choices once its connector is on screen.
  useEffect(() => {
    if (mode !== 'form' || !formConnector) return;
    (formConnector.configSchema?.fields || [])
      .filter((f) => f.type === 'SELECT' && f.choicesEndpoint)
      .forEach(async (f) => {
        try {
          const options = await deliveryService.fetchChoices(f.choicesEndpoint, envId, authenticatedFetch);
          setChoices((prev) => ({ ...prev, [f.key]: (options || []).map(normalizeChoice) }));
          setChoicesError((prev) => ({ ...prev, [f.key]: null }));
        } catch (e) {
          // An empty dropdown is indistinguishable from "you have no calendars", so say what
          // actually happened. The server's 409 here is the one an owner can act on: it names
          // the access that has not been granted.
          setChoices((prev) => ({ ...prev, [f.key]: [] }));
          setChoicesError((prev) => ({ ...prev, [f.key]: e.message || 'Could not load options' }));
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, formConnector, envId]);

  const handleClose = () => {
    stopPolling();
    onClose();
  };

  const openPicker = () => {
    resetForm();
    setMode('picker');
  };

  const selectConnector = async (connector) => {
    resetForm();
    setFormConnector(connector);
    const groups = eventGroupsFor(connector.supports);
    setFormEvents(new Set(groups[0]?.events || []));
    setMode('form');
    if (connector.connectionProvider) {
      const list = connections.length ? connections : await loadConnections();
      const existing = list.find((c) => c.provider === connector.connectionProvider);
      if (existing) setFormConnectionId(existing.id);
    }
  };

  const startEdit = (destination) => {
    const connector = connectors.find((c) => c.type === destination.type) || {
      type: destination.type,
      displayName: destination.type,
      supports: destination.events || [],
      connectionProvider: null,
      configSchema: { fields: [], helpText: null },
      requiredPlan: 'FREE',
    };
    setFormConnector(connector);
    setEditingDestination(destination);
    setFormValues({ ...(destination.config || {}) });
    setFormEvents(new Set(destination.events || []));
    setFormConnectionId(null);
    setFormError(null);
    setShowSecret({});
    setMode('form');
  };

  const closeForm = () => {
    stopPolling();
    resetForm();
    setMode('list');
  };

  const toggleGroup = (group, checked) => {
    setFormEvents((prev) => {
      const next = new Set(prev);
      group.events.forEach((e) => (checked ? next.add(e) : next.delete(e)));
      return next;
    });
  };

  const handleConnect = (provider) => {
    setFormError(null);
    // The popup reports back as soon as Google answers; the poll below is the fallback.
    openOAuthPopup(connectionsService.connectUrl(provider, envId), ({ result }) => {
      if (result === 'connected') {
        loadConnections().then((list) => {
          const found = list.find((c) => c.provider === provider);
          if (found) setFormConnectionId(found.id);
        }).finally(stopPolling);
        return;
      }
      setFormError(result === 'denied'
        ? 'Google access was not granted, so nothing is connected yet.'
        : 'Connecting your Google account failed. Try again.');
      stopPolling();
    });
    setConnecting(true);
    const startedAt = Date.now();
    const interval = setInterval(async () => {
      const list = await loadConnections();
      const found = list.find((c) => c.provider === provider);
      if (found) setFormConnectionId(found.id);
      if (found || Date.now() - startedAt > CONNECT_POLL_TIMEOUT_MS) {
        stopPolling();
      }
    }, CONNECT_POLL_MS);
    const timeout = setTimeout(stopPolling, CONNECT_POLL_TIMEOUT_MS);
    pollRef.current = { interval, timeout };
  };

  // Environment defaults this flow has no destination of its own type for, greyed rows in the
  // list, each offering "Override for this flow".
  const inheritedDefaults = useMemo(() => (isEnvScope ? [] : environmentDefaults.filter(
    (def) => !destinations.some((own) => own.type === def.type))),
  [isEnvScope, environmentDefaults, destinations]);

  const startOverride = async (defaultDestination) => {
    const connector = connectors.find((c) => c.type === defaultDestination.type);
    if (!connector) return;
    await selectConnector(connector);
    setFormValues({ ...(defaultDestination.config || {}) });
    if ((defaultDestination.events || []).length > 0) {
      setFormEvents(new Set(defaultDestination.events));
    }
  };

  const effectiveFields = useMemo(() => effectiveFieldsFor(formConnector), [formConnector]);
  const eventGroups = useMemo(
    () => (formConnector ? eventGroupsFor(formConnector.supports) : []),
    [formConnector],
  );

  const fieldsValid = useMemo(() => effectiveFields.every((f) => {
    if (f.type === 'REVEAL_ONCE') return true;
    if (f.type === 'CONNECTION') return !!formConnectionId;
    if (!f.required) return true;
    return String(formValues[f.key] ?? '').trim().length > 0;
  }), [effectiveFields, formConnectionId, formValues]);

  const eventsValid = editingDestination ? true : (eventGroups.length === 0 || formEvents.size > 0);
  const canSave = fieldsValid && eventsValid && !saving;

  const handleSave = async () => {
    setSaving(true);
    setFormError(null);
    try {
      if (editingDestination) {
        const config = {};
        effectiveFields.forEach((f) => {
          if (f.type === 'CONNECTION' || f.type === 'REVEAL_ONCE') return;
          config[f.key] = formValues[f.key];
        });
        await api.update(editingDestination.id, { config, enabled: editingDestination.enabled });
      } else {
        const config = {};
        effectiveFields.forEach((f) => {
          if (f.type === 'CONNECTION' || f.type === 'REVEAL_ONCE') return;
          if (formValues[f.key] !== undefined && formValues[f.key] !== '') {
            config[f.key] = formValues[f.key];
          }
        });
        const body = { type: formConnector.type, config, events: Array.from(formEvents) };
        if (formConnectionId) body.connectionId = formConnectionId;
        const created = await api.create(body);
        const revealField = effectiveFields.find((f) => f.type === 'REVEAL_ONCE');
        if (revealField) {
          const value = created?.[revealField.key] ?? created?.config?.[revealField.key];
          if (value) setRevealed({ label: revealField.label, value, help: revealField.help });
        }
        // Launch funnel event (docs/launch/ws-I.md). No email/URL/channel in the props, the
        // type is the fact worth knowing, not where the answers are going.
        track('destination_added', { type: created.type });
      }
      resetForm();
      setMode('list');
      await loadAll();
    } catch (e) {
      setFormError(e.message || 'Failed to save the destination');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleEnabled = async (destination) => {
    setError(null);
    try {
      await api.update(destination.id, { config: destination.config, enabled: !destination.enabled });
      await loadAll();
    } catch (e) {
      setError(e.message || 'Failed to update the destination');
    }
  };

  const handleDelete = async (destination) => {
    setError(null);
    try {
      await api.remove(destination.id);
      setDeleteConfirmId(null);
      await loadAll();
    } catch (e) {
      setError(e.message || 'Failed to delete the destination');
    }
  };

  const handleTest = async (destination) => {
    setTestingId(destination.id);
    setError(null);
    try {
      await api.test(destination.id);
      await loadAll();
    } catch (e) {
      setError(e.message || 'Test send failed');
    } finally {
      setTestingId(null);
    }
  };

  const handleCopySecret = async () => {
    try {
      await navigator.clipboard.writeText(revealed.value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access can be denied, the value stays visible to copy by hand.
    }
  };

  const displayNameFor = (type) => connectors.find((c) => c.type === type)?.displayName || type;

  const renderField = (field) => {
    if (field.type === 'REVEAL_ONCE') {
      if (editingDestination) return null;
      return (
        <Alert key={field.key} severity="info" icon={false} sx={{ py: 0.5 }}>
          <Typography variant="caption">
            {field.help || `${field.label} will be shown once after you save.`}
          </Typography>
        </Alert>
      );
    }

    if (field.type === 'CONNECTION') {
      const provider = formConnector.connectionProvider;
      const providerConns = connections.filter((c) => c.provider === provider);
      if (providerConns.length === 0) {
        return (
          <Box key={field.key} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Connect a {providerLabel(provider)} account to use {formConnector.displayName}.
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                size="small"
                variant="contained"
                onClick={() => handleConnect(provider)}
                disabled={connecting}
                data-testid={`connect-${provider}-button`}
              >
                {connecting ? 'Waiting…' : `Connect ${providerLabel(provider)}`}
              </Button>
              {connecting && <CircularProgress size={16} />}
            </Box>
          </Box>
        );
      }
      return (
        <FormControl key={field.key} fullWidth size="small">
          <InputLabel id={`${field.key}-label`}>{field.label}</InputLabel>
          <Select
            labelId={`${field.key}-label`}
            label={field.label}
            value={formConnectionId || providerConns[0].id}
            onChange={(e) => setFormConnectionId(e.target.value)}
            inputProps={{ 'data-testid': `field-${field.key}` }}
          >
            {providerConns.map((c) => (
              <MenuItem key={c.id} value={c.id}>{c.accountLabel || providerLabel(c.provider)}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    if (field.type === 'SELECT') {
      return (
        <FormControl key={field.key} fullWidth size="small">
          <InputLabel id={`${field.key}-label`}>{field.label}</InputLabel>
          <Select
            labelId={`${field.key}-label`}
            label={field.label}
            value={formValues[field.key] ?? ''}
            onChange={(e) => setFormValues((v) => ({ ...v, [field.key]: e.target.value }))}
            inputProps={{ 'data-testid': `field-${field.key}` }}
          >
            {(choices[field.key] || []).map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
          {choicesError[field.key] ? (
            <Typography
              variant="caption"
              color="error"
              sx={{ mt: 0.5 }}
              data-testid={`field-${field.key}-error`}
            >
              {choicesError[field.key]}
            </Typography>
          ) : field.help && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>{field.help}</Typography>
          )}
        </FormControl>
      );
    }

    const inputType = field.type === 'SECRET'
      ? (showSecret[field.key] ? 'text' : 'password')
      : field.type === 'EMAIL' ? 'email'
      : field.type === 'URL' ? 'url'
      : 'text';

    return (
      <TextField
        key={field.key}
        fullWidth
        size="small"
        type={inputType}
        multiline={field.type === 'MULTILINE'}
        minRows={field.type === 'MULTILINE' ? 3 : undefined}
        label={field.label}
        placeholder={field.placeholder || undefined}
        helperText={field.help || undefined}
        required={field.required}
        value={formValues[field.key] ?? ''}
        onChange={(e) => setFormValues((v) => ({ ...v, [field.key]: e.target.value }))}
        inputProps={{ maxLength: field.maxLength || undefined, 'data-testid': `field-${field.key}` }}
        InputProps={field.type === 'SECRET' ? {
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                size="small"
                edge="end"
                onClick={() => setShowSecret((s) => ({ ...s, [field.key]: !s[field.key] }))}
                aria-label={showSecret[field.key] ? 'Hide' : 'Show'}
              >
                {showSecret[field.key] ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
              </IconButton>
            </InputAdornment>
          ),
        } : undefined}
      />
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEnvScope ? 'Environment default destinations' : 'Where should answers go?'}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        <Typography variant="body2" color="text.secondary">
          {isEnvScope
            ? 'Applies to every flow in this environment unless the flow has its own destination '
              + 'of that type.'
            : `Every submission of “${flow?.name}” can be delivered to one or more `
              + 'destinations, in addition to appearing on the Submissions page.'}
        </Typography>

        {error && <Alert severity="error" onClose={() => setError(null)} data-testid="delivery-error">{error}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
        ) : mode === 'picker' ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => setMode('list')} sx={{ alignSelf: 'flex-start' }}>
              Back
            </Button>
            {connectorsError && <Alert severity="error">{connectorsError}</Alert>}
            <List disablePadding data-testid="connector-picker">
              {connectors.map((c) => {
                const locked = c.requiredPlan === 'PRO' && !features.deliveries;
                return (
                  <ListItemButton
                    key={c.type}
                    disabled={locked}
                    onClick={() => { if (!locked) selectConnector(c); }}
                    data-testid={`connector-option-${c.type}`}
                  >
                    <ListItemText
                      primary={(
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {iconFor(c.type)}
                          <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>
                            {c.displayName}
                          </Typography>
                          {c.requiredPlan === 'PRO' && <Chip size="small" label="Team" color="secondary" variant="outlined" />}
                        </Box>
                      )}
                      secondary={locked ? 'Requires the Team plan' : (c.configSchema?.helpText || null)}
                      primaryTypographyProps={{ component: 'div' }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </Box>
        ) : mode === 'form' && formConnector ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={editingDestination ? closeForm : () => { resetForm(); setMode('picker'); }}
              disabled={saving}
              sx={{ alignSelf: 'flex-start' }}
            >
              Back
            </Button>
            <Typography variant="subtitle2">{formConnector.displayName}</Typography>
            {formConnector.configSchema?.helpText && (
              <Typography variant="body2" color="text.secondary">{formConnector.configSchema.helpText}</Typography>
            )}
            {formError && <Alert severity="error" data-testid="destination-form-error">{formError}</Alert>}

            {effectiveFields.map((field) => renderField(field))}

            {!editingDestination && eventGroups.length > 0 && (
              <FormGroup row>
                {eventGroups.map((group) => (
                  <FormControlLabel
                    key={group.key}
                    control={(
                      <Checkbox
                        size="small"
                        checked={group.events.every((e) => formEvents.has(e))}
                        onChange={(e) => toggleGroup(group, e.target.checked)}
                        inputProps={{ 'data-testid': `event-${group.key}-checkbox` }}
                      />
                    )}
                    label={group.label}
                  />
                ))}
              </FormGroup>
            )}
            {editingDestination && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                {(editingDestination.events || []).map((e) => (
                  <Chip key={e} size="small" label={EVENT_LABELS[e] || e} />
                ))}
                <Typography variant="caption" color="text.secondary" sx={{ width: '100%' }}>
                  Events can&rsquo;t be changed after creation. Delete and add again to change what
                  this destination receives.
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button size="small" onClick={closeForm} disabled={saving}>Cancel</Button>
              <Button
                size="small"
                variant="contained"
                onClick={handleSave}
                disabled={!canSave}
                data-testid="save-destination-form"
              >
                {saving ? <CircularProgress size={16} /> : (editingDestination ? 'Save changes' : 'Add')}
              </Button>
            </Box>
          </Box>
        ) : (
          <>
            {destinations.length === 0 ? (
              <Alert severity="info">No destinations yet. Add one below.</Alert>
            ) : (
              <List dense disablePadding data-testid="destination-list">
                {destinations.map((d) => (
                  <ListItem
                    key={d.id}
                    divider
                    data-testid={`destination-row-${d.id}`}
                    secondaryAction={(
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Switch
                          size="small"
                          checked={!!d.enabled}
                          onChange={() => handleToggleEnabled(d)}
                          inputProps={{
                            'aria-label': `Enable ${displayNameFor(d.type)}`,
                            'data-testid': `destination-enabled-${d.id}`,
                          }}
                        />
                        <Tooltip title="Send test">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleTest(d)}
                              disabled={testingId === d.id}
                              data-testid={`send-test-${d.id}`}
                            >
                              {testingId === d.id ? <CircularProgress size={16} /> : <SendIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                        {isDestinationEditable(connectors.find((c) => c.type === d.type)) && (
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => startEdit(d)} data-testid={`edit-destination-${d.id}`}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => setDeleteConfirmId(d.id)}
                            data-testid={`delete-destination-${d.id}`}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  >
                    <ListItemText
                      primary={(
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          {iconFor(d.type)}
                          <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>
                            {displayNameFor(d.type)}
                          </Typography>
                          {/* summaryOf() can show an email address or a spreadsheet link, the
                              merchant's own destination, but excluded from recordings anyway. */}
                          {d.type === 'GOOGLE_SHEETS' && d.config?.spreadsheetUrl ? (
                            <Link
                              href={d.config.spreadsheetUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              variant="body2"
                              className="ph-no-capture"
                            >
                              {summaryOf(d)}
                            </Link>
                          ) : (
                            <Typography component="span" variant="body2" color="text.secondary" className="ph-no-capture">
                              {summaryOf(d)}
                            </Typography>
                          )}
                        </Box>
                      )}
                      secondary={(
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                          <Chip
                            size="small"
                            variant="outlined"
                            label={d.lastStatus || 'No deliveries yet'}
                            color={STATUS_COLOR[d.lastStatus] || 'default'}
                            data-testid={`destination-status-${d.id}`}
                          />
                          {d.lastDeliveryAt && (
                            <Typography variant="caption" color="text.secondary">{when(d.lastDeliveryAt)}</Typography>
                          )}
                          {(d.events || []).map((e) => (
                            <Chip key={e} size="small" label={EVENT_LABELS[e] || e} />
                          ))}
                          {isEnvScope && (
                            <Typography variant="caption" color="text.secondary" sx={{ width: '100%' }}>
                              {`Applies to every flow unless it has its own ${displayNameFor(d.type)} destination.`}
                            </Typography>
                          )}
                        </Box>
                      )}
                      primaryTypographyProps={{ component: 'div' }}
                      secondaryTypographyProps={{ component: 'div' }}
                    />
                  </ListItem>
                ))}
              </List>
            )}

            {!isEnvScope && inheritedDefaults.length > 0 && (
              <>
                <Typography variant="subtitle2">Inherited from environment defaults</Typography>
                <List dense disablePadding data-testid="inherited-defaults-list">
                  {inheritedDefaults.map((d) => (
                    <ListItem
                      key={d.id}
                      divider
                      sx={{ opacity: 0.6 }}
                      data-testid={`inherited-destination-${d.id}`}
                      secondaryAction={(
                        <Button
                          size="small"
                          onClick={() => startOverride(d)}
                          data-testid={`override-destination-${d.id}`}
                        >
                          Override for this flow
                        </Button>
                      )}
                    >
                      <ListItemText
                        primary={(
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {iconFor(d.type)}
                            <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>
                              {displayNameFor(d.type)}
                            </Typography>
                          </Box>
                        )}
                        secondary="Inherited from environment defaults"
                        primaryTypographyProps={{ component: 'div' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </>
            )}

            {deleteConfirmId && (() => {
              const d = destinations.find((x) => x.id === deleteConfirmId);
              if (!d) return null;
              return (
                <Alert
                  severity="warning"
                  action={(
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button size="small" onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
                      <Button size="small" color="error" onClick={() => handleDelete(d)} data-testid={`confirm-delete-${d.id}`}>
                        Delete
                      </Button>
                    </Box>
                  )}
                >
                  Remove this {displayNameFor(d.type)} destination?
                </Alert>
              );
            })()}

            {revealed && (
              <Alert
                severity="success"
                icon={false}
                action={(
                  <IconButton size="small" onClick={() => setRevealed(null)} aria-label="Dismiss">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {revealed.label} (shown once)
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }} data-testid="revealed-secret">
                    {revealed.value}
                  </Typography>
                  <Tooltip title={copied ? 'Copied!' : 'Copy'}>
                    <IconButton size="small" onClick={handleCopySecret} data-testid="copy-secret-button">
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                {revealed.help && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {revealed.help}
                  </Typography>
                )}
              </Alert>
            )}

            <Divider />

            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={openPicker}
              data-testid="open-add-destination"
            >
              Add a destination
            </Button>

            <Divider />

            <Typography variant="subtitle2">Recent deliveries</Typography>
            {deliveries.length === 0 ? (
              <Typography variant="caption" color="text.secondary">No delivery attempts yet.</Typography>
            ) : (
              <List dense disablePadding data-testid="recent-deliveries-list">
                {deliveries.map((attempt) => (
                  <ListItem key={attempt.id} divider disablePadding sx={{ py: 0.5 }}>
                    <ListItemText
                      primary={(
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            size="small"
                            variant="outlined"
                            label={attempt.status}
                            color={STATUS_COLOR[attempt.status] || 'default'}
                          />
                          <Typography variant="caption" color="text.secondary">{when(attempt.attemptedAt)}</Typography>
                        </Box>
                      )}
                      secondary={attempt.error || null}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DestinationDialog;
